// eslint-disable-next-line no-restricted-imports
import { z, type ZodArray, type ZodOptional, type ZodType } from 'zod'

/**
 * Nullish coalescing to undefined, applied via `.apply()`.
 * Preprocesses the input value, replacing `null` with `undefined`.
 * It is useful when you want to ensure that `null` values are treated
 * as `undefined` in your schema.
 *
 * @example
 * const schema = z.string().apply(nullishCoalescingUndefined);
 * schema.parse(null); // returns undefined
 * schema.parse(undefined); // returns undefined
 * schema.parse("hello"); // returns "hello"
 * schema.parse(123); // throws ZodError
 */
export const nullishCoalescingUndefined = <T extends ZodType>(schema: T) =>
  z.pipe(
    z.transform((val: unknown): unknown => val ?? undefined),
    schema.optional()
  ) as unknown as ZodOptional<
    ZodType<
      Exclude<z.output<T>, null> | undefined,
      z.input<T> | null | undefined,
      // @ts-expect-error // TODO: Fix internals Output mismatch
      T['_zod']
    >
  >

/**
 * Safe array parser, applied via `.apply()`.
 * Keeps only array items that are valid for the item schema.
 *
 * @example
 * const schema = z.array(z.object({ id: z.number() })).apply(safeFilteredArray);
 * schema.parse([{ id: 1 }, { id: 'x' }]); // returns [{ id: 1 }]
 *
 * @example
 * const schema = z.array(z.object({ id: z.number() })).apply(safeFilteredArray, {
 *   onError: (error, item) => console.warn('Invalid item', item, error.issues),
 * });
 * schema.parse([{ id: 1 }, { id: 'x' }]); // returns [{ id: 1 }], logs warning for { id: 'x' }
 */
export const safeFilteredArray = <T extends z.core.SomeType>(
  schema: ZodArray<T>,
  options?: { onError?: (error: z.ZodError, item: unknown) => void }
): ZodType<z.infer<T>[], unknown[]> => {
  const elementSchema = schema.element as unknown as ZodType<z.infer<T>>

  return z.array(z.unknown()).transform<z.infer<T>[]>((items: unknown[]) => {
    return items.reduce<z.infer<T>[]>((result, item) => {
      const parsedItem = elementSchema.safeParse(item)
      if (parsedItem.success) {
        result.push(parsedItem.data)
      } else {
        options?.onError?.(parsedItem.error, item)
      }

      return result
    }, [])
  })
}

export { z }
